/**
 * SuperTypeFilterDropdown - Super type filter built on the generic FilterDropdown.
 */
import type { FilterDropdownOption } from '@/components/FilterDropdown/FilterDropdown';
import FilterDropdown from '@/components/FilterDropdown/FilterDropdown';
import type { CardSuperType } from '@/types/card';
import type { ReactElement } from 'react';

export interface SuperTypeFilterDropdownProps {
  selectedSuperTypes: string[];
  onToggle: (superType: string) => void;
  onClear: () => void;
  className?: string;
}

const ICON_STYLE = { fontSize: '14px', color: '#d4a76a' } as const;

const SUPER_TYPE_ORDER: CardSuperType[] = ['legendary', 'snow', 'basic', 'world', 'ongoing'];

const SUPER_TYPE_ICONS: Record<CardSuperType, string> = {
  legendary: 'ms ms-ability-legendary ms-fw',
  snow: 'ms ms-s ms-fw',
  basic: 'ms ms-land ms-fw',
  world: 'ms ms-ability-domain ms-fw',
  ongoing: 'ms ms-ability-persist ms-fw',
};

const SUPER_TYPE_LABELS: Record<CardSuperType, string> = {
  legendary: 'Legendary',
  snow: 'Snow',
  basic: 'Basic',
  world: 'World',
  ongoing: 'Ongoing',
};

const SUPER_TYPE_OPTIONS: FilterDropdownOption[] = SUPER_TYPE_ORDER.map((superType) => ({
  value: superType,
  label: SUPER_TYPE_LABELS[superType],
  icon: <i className={SUPER_TYPE_ICONS[superType]} style={ICON_STYLE} aria-hidden="true" />,
  badgeClassName: 'bg-amber-500/20 text-amber-400',
}));

const TRIGGER_ICON = <i className="ms ms-ability-legendary ms-fw" style={{ fontSize: '14px' }} aria-hidden="true" />;

export default function SuperTypeFilterDropdown({
  selectedSuperTypes,
  onToggle,
  onClear,
  className,
}: SuperTypeFilterDropdownProps): ReactElement {
  return (
    <FilterDropdown
      triggerLabel="Supertypes"
      triggerIcon={TRIGGER_ICON}
      ariaLabel="Supertype filter"
      searchPlaceholder="Search supertypes..."
      options={SUPER_TYPE_OPTIONS}
      selectedValues={selectedSuperTypes}
      onToggle={onToggle}
      onClear={onClear}
      className={className}
    />
  );
}
